import { Row, Col, Card, Divider, Empty } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotify } from '../../hooks/useNotify';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import useQuerySocMembers from '../../hooks/useQuerySocMembers';
import useQueryUserById from '../../hooks/useQueryUserById';
import { onApolloError } from '../../lib/graphql/helpers';
import { userFullName } from '../../lib/utils';
import HeaderSection from '../HeaderSection';
import PageLoadingSpinner from '../PageLoadingSpinner';
import { UserRoster } from '../PersonalRoster';
import { Optional } from '../Shared';

const parenthesize = (str: string | undefined) =>
  str === undefined ? '' : ` (${str})`;

const InfoRow: React.FC<{
  readonly title: string;
  readonly content: JSX.Element;
}> = ({ title, content }) => (
  <div className="info-row">
    <Title level={5}>{title}</Title>
    <Divider style={{ marginTop: 0 }} />
    {content}
  </div>
);

/**
 * Displays the personnel information based on the selected user.
 */
const PersonnelInfo = () => {
  const { notify } = useNotify();
  const { userId } = useParams<{ readonly userId: string | undefined }>();

  const { data: userToDisplay } = useQueryUserById(
    onApolloError(notify.error),
    userId
  );

  // We will only show the roster if the logged in user is a SOC member.
  const { loggedInUser } = useLoggedInUser();
  const [socMemberIds, setSocMemberIds] = useState<readonly string[]>([]);
  const { loading: loadingSocMembers } = useQuerySocMembers(
    (ids) => setSocMemberIds(ids),
    onApolloError(notify.error)
  );

  if (
    loggedInUser === undefined ||
    userToDisplay?.user_by_pk === undefined ||
    loadingSocMembers
  ) {
    return <PageLoadingSpinner />;
  }

  const showRoster = socMemberIds.includes(loggedInUser.id);

  // The person whose information is being displayed.
  const personnel = userToDisplay.user_by_pk;
  const qualifications: readonly {
    readonly title: string;
    readonly id: string;
  }[] = personnel.group_membership.flatMap(({ group: { qualifications } }) =>
    qualifications.map((q) => ({
      title: `${q.name}${parenthesize(q.abbrev)}`,
      id: q.qualification_id,
    }))
  );

  return (
    <>
      <HeaderSection title={userFullName(personnel)} />
      <Row>
        <Col span="flex" style={{ margin: 'auto' }}>
          <Card>
            <InfoRow
              title="Personnel Details"
              content={
                <div id="personnel-details">
                  <p id="personnel-username">Username: {personnel.user_name}</p>
                  <p id="personnel-user-type">
                    Personnel Type: {personnel.user_type ?? <em>Unknown</em>}
                  </p>
                </div>
              }
            />

            <InfoRow
              title="Qualifications"
              content={
                qualifications.length === 0 ? (
                  <Empty />
                ) : (
                  <div id="qualification-list">
                    {qualifications.map(({ id, title }) => (
                      <p key={id}>{title}</p>
                    ))}
                  </div>
                )
              }
            />

            <Optional show={showRoster}>
              <InfoRow
                title="Roster"
                content={<UserRoster userId={personnel.id} />}
              />
            </Optional>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PersonnelInfo;
